import React, { useId } from "react";
import { useSelector } from "react-redux";
import { Navigate, useParams } from "react-router-dom";
import styled from "styled-components/macro";

import Stack from "../../../components/Stack";
import ScrollToTop from "../../../components/utils/ScrollToTop";
import { TrayCell, TrayCells } from "../../../probate/components/tray";
import { useRefreshDashboard, useSelectedCase } from "../utils/hooks";
import {
  collectionDoables,
  collectionPath,
  collectionsOf,
  collectionSections,
  doableStatus,
  isDone,
} from "./requestGroupCollections";
import { canOpenDoable, doableHref, formDoableTypes } from "./doableLinks";
import ProbatePage from "./ProbatePage";
import { SheetBody } from "./Sheet";
import StatusMarker from "./StatusMarker";

// Sections and the count space themselves
const Body = styled(SheetBody)`
  gap: 0;
  padding-bottom: var(--juno-space-6);
`;

const Description = styled(Stack)`
  padding-bottom: var(--juno-space-2);

  p:first-child {
    font-weight: var(--juno-font-weight-bold);
  }
`;

const SectionTitle = styled.h2`
  margin: 0;
  padding: var(--juno-space-4) 0 var(--juno-space-1);
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);
  font-weight: var(--juno-font-weight-bold);
  color: var(--muted-foreground);
`;

const DoneCount = styled.p`
  margin: 0;
  padding-top: var(--juno-space-6);
  text-align: center;
  color: var(--muted-foreground);
`;

// As Things to do and Everything you’ve done link them. ProbatePage reads back for its back link.
const linkProps = (doable, canOpen, back) => {
  if (isDone(doable)) {
    return formDoableTypes.includes(doable.doable_type) ? { href: doableHref(doable), state: { back } } : {};
  }
  return canOpen(doable) ? { href: doableHref(doable), state: { fromDashboard: true, back } } : { disabled: true };
};

const Section = ({ section, canOpen, back }) => {
  const titleId = useId();
  return (
    <div>
      {section.title && <SectionTitle id={titleId}>{section.title}</SectionTitle>}
      <TrayCells aria-labelledby={section.title ? titleId : undefined}>
        {section.doables.map((doable) => (
          <TrayCell
            key={doable.id}
            title={doable.request_group_collection?.label ?? doable.title}
            status={<StatusMarker status={doableStatus(doable)} />}
            {...linkProps(doable, canOpen, back)}
          />
        ))}
      </TrayCells>
    </div>
  );
};

const RequestGroupCollectionPage = () => {
  const session = useSelector((state) => state.dashboardState.session);
  const selectedCase = useSelectedCase({ caseScreen: "case" });
  const { collectionIdentifier } = useParams();
  useRefreshDashboard({ staleTime: 0 });

  if (!selectedCase) {
    return null;
  }

  const casePath = `/dashboard/${selectedCase.case_reference}`;
  const collection = collectionsOf(selectedCase).find((c) => c.identifier === collectionIdentifier);
  const doables = collection ? collectionDoables(collection, selectedCase.doables) : [];
  if (doables.length === 0) {
    return <Navigate to={casePath} replace />;
  }

  const canOpen = (doable) => canOpenDoable(session, doable);
  const back = { to: collectionPath(selectedCase.case_reference, collection), label: collection.title };

  return (
    <ProbatePage caseRef={selectedCase.case_reference} title={collection.title}>
      <ScrollToTop />
      <Body>
        {collection.description?.length > 0 && (
          <Description gap="sm">
            {collection.description.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </Description>
        )}
        {collectionSections(collection, doables)
          .filter((section) => section.doables.length > 0)
          .map((section) => (
            <Section key={section.identifier} section={section} canOpen={canOpen} back={back} />
          ))}
        <DoneCount>
          {doables.filter(isDone).length} of {doables.length} done
        </DoneCount>
      </Body>
    </ProbatePage>
  );
};

export default RequestGroupCollectionPage;
